import { useState, useCallback } from 'react';
import { PickerType } from '../global-state/types';
import {
  formatRangeLabel,
  isDateInWindow,
  isRangeMode,
  isSameDate,
  orderDates,
  validateRange,
} from '../stories/molecules/date-picker/utils';

type RangeSelectionResult = {
  shouldClose: boolean;
  label?: string;
  isValid?: boolean;
};

export const useRangeSelection = (
  pickerType: PickerType,
  onRangeChange?: (dates: [Date, Date], label: string) => void,
  onAccept?: (dates: [Date, Date]) => void,
) => {
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [isValidRange, setIsValidRange] = useState(true);

  const isRangePicker = isRangeMode(pickerType);

  const isDateInSelectableWindow = useCallback(
    (date: Date) => {
      if (!isRangePicker || !rangeStart || rangeEnd) return true;
      return isDateInWindow(date, rangeStart);
    },
    [isRangePicker, rangeStart, rangeEnd],
  );

  const handleRangeSelection = useCallback(
    (date: Date | null): RangeSelectionResult => {
      if (!isRangePicker || !date) return { shouldClose: false };

      // start new selection
      if (!rangeStart || rangeEnd) {
        setRangeStart(date);
        setRangeEnd(null);
        setIsValidRange(true);
        return { shouldClose: false, label: 'Select end date', isValid: true };
      }

      // invalid selection (outside window)
      if (!isDateInSelectableWindow(date)) {
        return { shouldClose: false, isValid: false };
      }

      const [startDate, endDate] = orderDates(rangeStart, date);
      const isValid = validateRange(startDate, endDate);

      setRangeEnd(endDate);
      setIsValidRange(isValid);

      if (isValid) {
        const label = formatRangeLabel(startDate, endDate);
        onRangeChange?.([startDate, endDate], label);
        onAccept?.([startDate, endDate]);
        return { shouldClose: true, label, isValid: true };
      }

      return { shouldClose: false, isValid: false };
    },
    [isRangePicker, rangeStart, rangeEnd, onRangeChange, onAccept, isDateInSelectableWindow],
  );

  const resetRange = useCallback(() => {
    if (isRangePicker) {
      setRangeStart(null);
      setRangeEnd(null);
      setIsValidRange(true);
    }
  }, [isRangePicker]);

  const isDateInRange = useCallback(
    (date: Date) => {
      if (!isRangePicker || !rangeStart || !rangeEnd) return false;
      const [start, end] = orderDates(rangeStart, rangeEnd);
      return date >= start && date <= end;
    },
    [isRangePicker, rangeStart, rangeEnd],
  );

  const isDateRangeStart = useCallback(
    (date: Date) => isRangePicker && isSameDate(rangeStart, date),
    [isRangePicker, rangeStart],
  );

  const isDateRangeEnd = useCallback(
    (date: Date) => isRangePicker && isSameDate(rangeStart, date),
    [isRangePicker, rangeEnd],
  );

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (!isRangePicker || !rangeStart || rangeEnd) return false;
      return !isDateInSelectableWindow(date);
    },
    [isRangePicker, rangeStart, rangeEnd, isDateInSelectableWindow],
  );

  return {
    rangeStart,
    rangeEnd,
    isValidRange,
    handleRangeSelection,
    resetRange,
    isDateInRange,
    isDateRangeStart,
    isDateRangeEnd,
    isDateDisabled,
    isDateInSelectableWindow,
  };
};
